import { useEffect, useRef } from 'react'
import { datadogRum } from '@datadog/browser-rum'
import {
  CHECKOUT_RULE_DOCUMENT_TYPES,
  CheckoutRules,
  type CheckoutRuleDocumentType,
} from '../../interfaces'
import {
  selectAutoSettleStatus,
  selectAvailableByRuleType,
  setAutoSettleStatus,
} from '../../redux/slices/checkout'
import type { AutoSettleStatus } from '../../redux/slices/checkout/types'
import type { ICheckoutRuleValue } from '../../graphQL/queries/getCheckoutRule/types'
import { AlertMessageType } from '../../utils/getAlertErrorMessage'
import {
  isUsableOption,
  selectRulePickId,
} from '../../utils/checkoutRuleSelection'
import { generateDataDogContext } from '../../utils/dataDog'
import { fireCheckoutRuleResolved } from '../../utils/fireCheckoutRuleResolved'
import type { RootState } from '../../redux/store'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import useAppStore from '../useAppStore'
import useCheckoutRuleOptions from './useCheckoutRuleOptions'
import useCheckoutRuleSelection from './useCheckoutRuleSelection'

const FUNCTION_NAME = 'useCheckoutRuleAutoSettle'

/**
 * How long one rule gets to decide before the shopper is let through.
 *
 * Apollo's `HttpLink` carries no timeout, so without this a request that never
 * settles leaves the status at `'resolving'`, `rulesResolving` at `true`, and
 * the footer button disabled for the rest of the session — on the one screen
 * where an order gets placed. The deadline turns that into a shopper who
 * checks out and a Datadog error saying why.
 *
 * **One budget per leg, not one per decision.** It is armed when the fetch
 * starts and re-armed when the write starts, so a slow fetch cannot eat the
 * write's allowance. Eight seconds is long enough that a slow network does not
 * routinely spend it, short enough that a shopper who has decided to buy is
 * not left staring at a dead button.
 *
 * ⚠️ **Firing during the write frees the CTA with the mutation still open, and
 * that is the deliberate trade.** The alternative is holding the button for
 * ever on a request that never settles. So the deadline settles the rule
 * `'failed'` rather than `'settled'`: the rule reads unsettled, COM-1046's
 * queue keeps it, and the shopper is routed to the picker to choose by hand —
 * which is the recovery COM-1048 specifies for a write that does not land.
 */
export const AUTO_SETTLE_DEADLINE_MS = 8000

/** The toast copy each rule's write would raise, were it not silenced. */
export const ALERT_BY_RULE: Record<CheckoutRuleDocumentType, AlertMessageType> =
  {
    [CheckoutRules.DELIVERY_DATES]: AlertMessageType.SetDeliveryDate,
    [CheckoutRules.PAYMENT_METHODS]: AlertMessageType.SetPaymentMethod,
  }

const isTransient = (status?: AutoSettleStatus): boolean =>
  status === 'resolving' || status === 'writing'

/**
 * Reports a rule that offers the shopper nothing.
 *
 * **Live on the pilot**, per the COM-736 convention of saying which branches
 * can actually fire: `validateDates` drops the one day `resolveDefaultDates`
 * computed unless it falls strictly after today, so a `DELIVERY_DATES` rule
 * with no options is an ordinary morning rather than a broken storefront.
 *
 * Reported here and nowhere else. The sibling error path (unknown rule
 * type, 5xx, network failure) is already reported by
 * `useCheckoutRuleOptions.onError`, so this branch skips a second report
 * to Error Tracking. Post-COM-1076 the two "nothing offered" paths no
 * longer collapse — this branch is the shopper-eligibility signal; the
 * error path is `'unavailable'`. Full split in
 * `graphQL/queries/getCheckoutRule/query.ts`.
 */
type ReportEmptyArgs = {
  ruleType: CheckoutRuleDocumentType
  state: RootState
  functionName: string
}

const reportEmpty = ({ ruleType, state, functionName }: ReportEmptyArgs) => {
  const context = generateDataDogContext({
    title: 'a checkout rule offers nothing and cannot gate the order',
    extraInfo: {
      function: functionName,
      ruleType,
      authoredRoute: JSON.stringify(
        state.defaultSlice.config?.checkoutRuleRoutes?.[ruleType] ?? null,
      ),
    },
  })
  datadogRum.startView(context.viewName)
  datadogRum.addError(
    new Error(
      `checkout rule ${ruleType} returned no usable option, so it is exempted from gating the order`,
    ),
    context,
  )
}

/**
 * Reports a `preselected` value that does not name any option in `usable`.
 *
 * Yao's `getCheckoutRule` guide (§7): `preselected` "is always one of the
 * options in the list, or `null`". So this fires only on a backend drift —
 * the storefront's authored default was withdrawn from the list after the
 * fact, or the shared lookup (COM-1076) returned inconsistent data. Either
 * way, it is the storefront-side signal that the invariant broke.
 *
 * The decision falls through on a drift rather than writing the stale value:
 * a `setCheckoutRuleSession` carrying a value the backend never offered is
 * exactly the `selection_not_offered` rejection COM-1078 adds. On a
 * two-plus-option list the fallthrough lands at `'multiple'` and the shopper
 * picks; on a one-option list it takes the one option, which is reasonable
 * rather than correct.
 */
type ReportPreselectedMissArgs = {
  ruleType: CheckoutRuleDocumentType
  preselected: string
  functionName: string
}

const reportPreselectedMiss = ({
  ruleType,
  preselected,
  functionName,
}: ReportPreselectedMissArgs) => {
  const context = generateDataDogContext({
    title: "a checkout rule's preselection names an option it no longer offers",
    extraInfo: {
      function: functionName,
      ruleType,
      preselected,
    },
  })
  datadogRum.startView(context.viewName)
  datadogRum.addError(
    new Error(
      `checkout rule ${ruleType} preselected ${preselected}, which is not among the usable options; the preselection is dropped`,
    ),
    context,
  )
}

/** What one rule's loaded options settle to. */
export type AutoSettleDecision =
  /**
   * Settle without writing anything.
   *
   * `resolution` and `optionCount` are the facets the hook forwards to
   * `commerce-checkout_rule_resolved` (COM-1528). The decision function is
   * the single place that knows which branch fired, so it is also where the
   * GA4 facet is picked — anything else would have to re-run the branching.
   */
  | {
      status: 'empty' | 'settled' | 'multiple'
      resolution: 'no_options' | 'preselected' | 'pick_needed'
      optionCount: number
    }
  /**
   * Write `write` (an option's `key ?? data`), then settle `terminal` if the
   * write lands and `'failed'` if it does not. The hook picks the GA4
   * facet from the write outcome (`auto_selected` or `failed`).
   */
  | { write: string; terminal: 'settled' | 'multiple'; optionCount: number }

export interface DecideAutoSettleArgs {
  ruleType: CheckoutRuleDocumentType
  /** The options the fetch returned, unfiltered. */
  data: ICheckoutRuleValue[]
  /** The fetch's `preselected`, `null` when it carried none. */
  preselected: string | null
  /** Read fresh by the caller, for the shopper's own picks. */
  state: RootState
  /** Names the caller in the two Datadog reports. */
  functionName: string
}

/**
 * Decides what one rule settles to from a **loaded** answer: the offered
 * options and the shopper's own pick (COM-1048, COM-1184).
 *
 * Shared by `useAutoSettleCheckoutRule` (cart and summary) and COM-1501's
 * `startGuidedWalk` (the catalog), so the two cannot disagree about a rule.
 * The callers own everything around it — the fetch, the status latch, the
 * deadline, the write — and differ there on purpose: see each one. A failed
 * fetch never reaches this, because the two map it differently
 * (`'unavailable'` here, `'failed'` in the walk).
 *
 * Not pure: it raises the `'empty'` and preselection-drift reports, the same
 * two the hook raised inline before the extraction.
 */
export const decideAutoSettle = ({
  ruleType,
  data,
  preselected,
  state,
  functionName,
}: DecideAutoSettleArgs): AutoSettleDecision => {
  const usable = data.filter(isUsableOption)

  if (usable.length === 0) {
    reportEmpty({ ruleType, state, functionName })
    return { status: 'empty', resolution: 'no_options', optionCount: 0 }
  }

  /*
   * A shopper's own pick wins over everything else.
   *
   * Yao's `getCheckoutRule` guide (§7): "A shopper's own pick always wins
   * over `preselected`." Checked ahead of the preselection and the
   * length-1 branches so neither can overwrite a choice the shopper
   * already made. The pick is matched by `(option.key ?? option.data)`,
   * which is also what the picker's `id` and the backend's `preselected`
   * are — Yao's §4 and §7. On a keyed list that is the `key`; on a
   * non-keyed list it is the `data`. Yao's §4: "A list is either all keyed
   * or not keyed at all", so the two cannot ambiguate.
   *
   * Both branches read the pick as `key ?? data`. The fresh selection does
   * so straight off the slice; the persisted fallback does so through
   * `selectRulePickId`, which reads the slice first and `sessionData`
   * behind it (COM-1253 — same precedence as `selectRulePick`, and the
   * sibling the server-copied `key` now round-trips through). Reading the
   * persisted branch on `data` alone treated a saved `CREDIT_60D` as
   * offered whenever any `CREDIT` option was offered, so a shopper whose
   * saved key had been withdrawn kept the stale pick instead of flowing
   * through the stale-pick branch below.
   *
   * Only a pick that is **in the current list** counts. A pick the
   * storefront no longer offers — a delivery day computed from today the
   * shopper first saw yesterday, say — falls through to the branches
   * below, which overwrite it with whatever the storefront offers now.
   */
  const freshPick = state.checkoutSlice.selections[ruleType]
  const freshPickValue = freshPick?.key ?? freshPick?.data
  const freshPickIsOffered = freshPickValue
    ? usable.some((option) => (option.key ?? option.data) === freshPickValue)
    : false
  const persistedPickValue = freshPickValue
    ? undefined
    : selectRulePickId(state, ruleType)
  const persistedPickIsOffered = persistedPickValue
    ? usable.some(
        (option) => (option.key ?? option.data) === persistedPickValue,
      )
    : false

  if (freshPickIsOffered || persistedPickIsOffered) {
    /*
     * On a 2+ list, keep the Change control visible: `cartCheckoutPanel.ts`
     * puts `'settled'` in `SUPPRESSES_CHANGE`, and landing there with two
     * usable options locks the shopper into their first pick. `'multiple'`
     * is terminal here too — the latch above (`status !== undefined &&
     * status !== 'resolving'`) rejects a re-entry on any subsequent
     * fetch, so this does not re-prompt the shopper. (Review of #810,
     * 2026-10-06.)
     */
    return {
      status: usable.length > 1 ? 'multiple' : 'settled',
      resolution: 'preselected',
      optionCount: usable.length,
    }
  }

  /*
   * Prefer the backend's `preselected` to the fallback below.
   *
   * Yao's §4: `preselected` is the storefront's default pick, and
   * auto-saving it on screen load is what makes a one-tap checkout work.
   * The `usable[0]` fallback is a strict subset — a single-option list
   * implicitly preselects that option — kept because the pilot's current
   * response has `preselected: null` for both rules until a storefront
   * authors `paymentRules.preselected` or ships a `GET_CHECKOUT_RULE`
   * add-on. Either path converges on the same `select()` latch below.
   *
   * **Match by `key ?? data`.** Storefront `v3.43.0` returns the option's
   * `key` as `preselected` when the options are keyed
   * (`[{data:'CREDIT',key:'CREDIT_14D'}, {data:'CREDIT',key:'CREDIT_30D'}]`
   * with `preselected: 'CREDIT_30D'`), so an equality on `option.data`
   * never matches on a keyed list — no write, settles `'multiple'`, false
   * drift on every cart load. Review of #810 (2026-10-06) caught this.
   * The lookup matches the pattern PR 2 uses on the write path for the
   * same reason, pinned by a key-wins-over-data test on both branches.
   *
   * **A `preselected` that does not name any usable option is a drift**
   * (Yao's §7: `preselected` is always one of the options, or `null`),
   * reported through `reportPreselectedMiss` and then dropped — the normal
   * branching below handles both outcomes (route the shopper to the picker
   * when there are 2+ options, take the one option when there is 1).
   */
  const preselection = preselected
    ? usable.find((option) => (option.key ?? option.data) === preselected)
    : undefined

  if (preselected && !preselection) {
    reportPreselectedMiss({ ruleType, preselected, functionName })
  }

  if (!preselection && usable.length > 1) {
    return {
      status: 'multiple',
      resolution: 'pick_needed',
      optionCount: usable.length,
    }
  }

  const chosen = preselection ?? usable[0]

  /*
   * Same `'multiple'` vs `'settled'` rule as the shopper-pick branch above:
   * a 2+ list where `preselected` wrote one option must leave the Change
   * control visible, so the shopper is not locked in to the preselection.
   * (Review of #810, 2026-10-06.)
   *
   * `(chosen.key ?? chosen.data)` is the id the write path expects. On a
   * keyed payment list it names the specific `CREDIT_14D` or `CREDIT_30D`
   * row; on a non-keyed list and on `DELIVERY_DATES` (never keyed) it is
   * the `data`.
   */
  return {
    write: chosen.key ?? chosen.data,
    terminal: usable.length > 1 ? 'multiple' : 'settled',
    optionCount: usable.length,
  }
}

/**
 * Decides one rule's settle state from the offered options and the shopper's
 * own pick, so the shopper never sees a screen that cannot change anything.
 *
 * COM-1048 took the only option on single-option rules. COM-1184 extended the
 * same hook with two more cases, and the three now cover what the shopper
 * sees on the cart entry:
 *
 * 1. **One usable option** — write it (COM-1048 case). Settles `'settled'`.
 * 2. **A still-offered shopper pick** — keep it (COM-1184 case, Yao §7). On a
 *    1-option list that pick is the only option; on a 2+ list the shopper
 *    chose from them. Settles `'settled'` on the first, `'multiple'` on the
 *    second, so the Change control stays visible for a shopper who can still
 *    change their mind.
 * 3. **A backend `preselected`** — write it when the shopper has not picked
 *    (COM-1184 case, Yao §4). On a 1-option list the single-option branch
 *    had already settled it; on a 2+ list this is the storefront's default,
 *    and the Change control stays visible for the same reason as case 2.
 *
 * On the pilot this is the common case rather than a corner: `PAYMENT_METHODS`
 * returns one method and `DELIVERY_DATES` returns at most one day, because
 * `getCheckoutRule` reaches no external-API branch and every answer falls to
 * `resolveDefaultDates`. Without this the shopper taps through two screens
 * that each offer one choice.
 *
 * It owns no fetch and no mutation of its own. The read is
 * `useCheckoutRuleOptions`, the write is `useCheckoutRuleSelection`, and this
 * hook is the decision between them. The decision on a loaded answer is
 * `decideAutoSettle` above, shared with COM-1501's `startGuidedWalk`; the
 * latch, deadline and error mapping around it stay here. What it adds is the status in
 * `checkoutSlice.autoSettle` — see `AutoSettleStatus` for the table, which is
 * also what `utils/cartCheckoutPanel.ts` and COM-1046's queue read.
 *
 * **A storefront "serves" a rule when it authors
 * `config.checkoutRuleRoutes[ruleType]`.** That map already exists, already
 * means "here is the screen for this rule", and is what COM-950 and COM-1045
 * key off. Keying on it is also what keeps M1-C's read-only storefront
 * untouched: no route, no fetch, no write, rows exactly as they render today.
 * No storefront authors one until COM-951, so this hook is inert fleet-wide.
 *
 * ⚠️ **One live instance per rule type, which is stricter than "once per
 * screen".** `useCheckoutRuleOptions` carries that contract and this mounts
 * one. Two screens mounting it is fine and expected, because `<Routes>`
 * renders one matched route and `BaseContainer` picks one template — so the
 * mounts are sequential and the second finds the decision already made in the
 * store. Two instances **alive at once** would not be fine: both would read
 * `'resolving'` in the same commit, and `advance` refuses only a terminal
 * status, so both would enter `'writing'` and call `select`. The store latch
 * orders writes across time, not within one commit.
 */
export const useAutoSettleCheckoutRule = (
  ruleType: CheckoutRuleDocumentType,
) => {
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const deadlineRef = useRef<ReturnType<typeof setTimeout>>()

  const served = useAppSelector(
    (state) => !!state.defaultSlice.config?.checkoutRuleRoutes?.[ruleType],
  )
  const sessionId = useAppSelector((state) => state.defaultSlice.sessionId)
  const status = useAppSelector(selectAutoSettleStatus(ruleType))
  const available = useAppSelector(selectAvailableByRuleType(ruleType))

  // COM-1528: `commerce-checkout_rule_resolved` fires at most once per session
  // and rule across every producer — this hook and the catalog-guided walk
  // (`useStartGuidedWalk`) both race to be the first terminal writer, and a
  // per-hook ref cannot coordinate them. `fireCheckoutRuleResolved` holds a
  // module-level `(sessionId, ruleType)` set and resets on a session change.
  const fireResolved = (
    resolution: Parameters<typeof fireCheckoutRuleResolved>[0]['resolution'],
    optionCount: number,
  ) => {
    fireCheckoutRuleResolved({ ruleType, resolution, optionCount, sessionId })
  }

  /*
   * Ask only while the answer can still change anything.
   *
   * `'resolving'` keeps it open on purpose. The redirect in
   * `useCheckoutRulePreflight` is mounted on this same screen and navigates
   * from its own `onCompleted`, which unmounts the cart mid-flight — Apollo
   * tears the observable down and neither callback ever fires. Leaving the
   * gate open for `'resolving'` is what makes the next mount re-ask instead of
   * inheriting a fetch that died.
   */
  useCheckoutRuleOptions(
    ruleType,
    served && (status === undefined || status === 'resolving'),
  )

  const { select } = useCheckoutRuleSelection(
    ruleType,
    ALERT_BY_RULE[ruleType],
    {
      silent: true,
    },
  )

  const clearDeadline = () => {
    if (deadlineRef.current === undefined) return
    clearTimeout(deadlineRef.current)
    deadlineRef.current = undefined
  }

  /**
   * Moves this rule's status, unless something already decided it.
   *
   * Every write goes through here, because two of them land from callbacks a
   * round trip after their closure was built — the deadline's timer and
   * `select`'s promise — and either can arrive after the other has settled the
   * rule. The current status is read fresh from the store for that reason
   * (COM-974's rule), and a terminal one is never overwritten. That is what
   * keeps `AutoSettleStatus` monotonic, which is in turn what lets the effect
   * latch and write once per session.
   */
  const advance = (next: AutoSettleStatus) => {
    const current = selectAutoSettleStatus(ruleType)(store.getState())
    if (current !== undefined && !isTransient(current)) return

    /*
     * Each transient leg gets its own deadline. Entering `'writing'` restarts
     * the clock rather than inheriting what the fetch left, so a fetch that
     * took seven seconds does not leave the write one.
     */
    clearDeadline()
    if (isTransient(next)) armDeadline()

    dispatch(setAutoSettleStatus({ ruleType, status: next }))
  }

  /** Idempotent: a live timer is left alone rather than restarted. */
  const armDeadline = () => {
    if (deadlineRef.current !== undefined) return

    deadlineRef.current = setTimeout(() => {
      deadlineRef.current = undefined

      /*
       * Which leg ran out decides the outcome, so read it rather than assuming.
       *
       * `'writing'` means the mutation is still open. Settling that as
       * `'unavailable'` would free the CTA **and** drop the rule out of
       * COM-1046's queue, letting the shopper place an order over a session
       * that may never carry the rule — the exact race `'writing'` exists to
       * prevent, reached through the deadline instead of the option count.
       * `'failed'` frees the CTA and keeps the rule in the queue, so the
       * picker still gets them.
       *
       * A rule that settled while this timer was pending needs neither the
       * status nor the report. `advance` would refuse the write anyway; the
       * early return is what stops the Datadog error firing beside it.
       */
      const current = selectAutoSettleStatus(ruleType)(store.getState())
      if (!isTransient(current)) return

      const outcome: AutoSettleStatus =
        current === 'writing' ? 'failed' : 'unavailable'

      // GA4 bucket: both deadline outcomes read as `failed` to the shopper.
      const usableAtDeadline = (
        selectAvailableByRuleType(ruleType)(store.getState())?.data ?? []
      ).filter(isUsableOption)
      fireResolved('failed', usableAtDeadline.length)

      const context = generateDataDogContext({
        title:
          'a checkout rule did not resolve before its auto-settle deadline',
        extraInfo: {
          function: FUNCTION_NAME,
          ruleType,
          deadlineMs: AUTO_SETTLE_DEADLINE_MS,
          interrupted: current,
          outcome,
        },
      })
      datadogRum.startView(context.viewName)
      datadogRum.addError(
        new Error(
          `checkout rule ${ruleType} was still ${current} after ${AUTO_SETTLE_DEADLINE_MS}ms; the shopper is let through and the rule is left ${outcome}`,
        ),
        context,
      )

      advance(outcome)
    }, AUTO_SETTLE_DEADLINE_MS)
  }

  /**
   * Decide once, and hold the CTA while deciding.
   *
   * ⚠️ **`served` is `false` on the first commit of every storefront, and the
   * guard has to run before any dispatch.** `config` is dispatched from an
   * effect in `Config` and effects run children-first, the same fact
   * `useCheckoutRuleOptions` and `useCheckoutRulePreflight` both document for
   * `sessionId`. Dispatch `'resolving'` ahead of this test and every
   * storefront in the estate sits at `rulesResolving: true` with no fetch that
   * could ever clear it — and no storefront authors `checkoutRuleRoutes` until
   * COM-951, so that would be all of them.
   *
   * **`select` is deliberately not a dependency**, and the reason is cost
   * rather than correctness. `useCheckoutRuleSelection` builds a fresh closure
   * every render, so depending on it would re-run this effect on every render
   * — and every one of those runs would fall out at a guard, because the
   * monotonic `status` latch below and the shopper-pick check already cover
   * it. Measured rather than assumed: adding `select` to this array leaves
   * the whole suite green. It is omitted to keep the effect from running for
   * nothing, under the same `exhaustive-deps` exemption both sibling hooks
   * carry. Do not read it as the thing standing between the shopper and a
   * second write.
   *
   * The dependencies read `available?.loadingState`, `available?.data` and
   * `available?.preselected` rather than `available`, because `setLoadingState`
   * spreads into a fresh object on every call and the whole entry re-identifies
   * each time. `preselected` moves on its own with the fetch (COM-1184 PR 1),
   * which is why it earns its own slot alongside the other two.
   */
  useEffect(() => {
    if (!served) return
    if (status !== undefined && status !== 'resolving') return
    if (sessionId === undefined) return

    armDeadline()

    if (status === undefined) {
      advance('resolving')
      return
    }

    const loadingState = available?.loadingState
    if (loadingState !== 'loaded' && loadingState !== 'error') return

    if (loadingState === 'error') {
      advance('unavailable')
      fireResolved('failed', 0)
      return
    }

    const decision = decideAutoSettle({
      ruleType,
      data: available?.data ?? [],
      preselected: available?.preselected ?? null,
      state: store.getState(),
      functionName: FUNCTION_NAME,
    })

    if (!('write' in decision)) {
      advance(decision.status)
      fireResolved(decision.resolution, decision.optionCount)
      return
    }

    /*
     * The latch, entered synchronously before the write goes out.
     *
     * This is what makes the write once per session: the next run of this
     * effect returns at the status guard above. It carries the case the
     * shopper-pick check cannot — a **rejected** write leaves no pick behind,
     * so only `'failed'` being terminal stops a later refetch trying again.
     * It is also the second half of the CTA hold, because `'writing'` keeps
     * `rulesResolving` raised until the mutation lands.
     */
    advance('writing')
    /*
     * A late outcome is still refused here: if the deadline already settled
     * this `'failed'`, `advance` will not write the terminal value over it.
     * But it no longer decides on its own (COM-1501). When the late write
     * lands, `select` itself releases the `'failed'` rule
     * (`releaseFailedAutoSettle`), because the value is now on the session
     * and keeping `'failed'` would loop the shopper through the picker. So
     * the summary CTA can flip to place-order after the deadline, which was
     * signed off as the lesser cost. The optimistic `setSelection` is
     * untouched either way, so the written value shows on the cart row.
     */
    select(decision.write).then((written) => {
      advance(written ? decision.terminal : 'failed')
      fireResolved(written ? 'auto_selected' : 'failed', decision.optionCount)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    served,
    sessionId,
    status,
    available?.loadingState,
    available?.data,
    available?.preselected,
  ])

  /*
   * Drop the timer on the way out, and let the next mount arm its own.
   *
   * Leaving it running would settle the rule `'unavailable'` for a shopper who
   * simply walked away from the cart — terminal, so no later visit would
   * re-ask. Dropping it leaves the status at `'resolving'`, which is exactly
   * the state the fetch gate above re-opens on.
   */
  useEffect(() => clearDeadline, [])
}

/**
 * Settles every checkout rule that offers exactly one value.
 *
 * Mounted by `templates/Cart.tsx` and `templates/Summary.tsx` — the two
 * screens where the answer is worth having before the shopper commits, and the
 * two that render a footer bound to `checkout.rulesResolving`.
 *
 * The two rule types are written out rather than mapped, because a `.map` over
 * `CHECKOUT_RULE_DOCUMENT_TYPES` calls a hook in a loop. `EventManager` writes
 * out its two actions hooks for the same reason.
 */
export const useCheckoutRuleAutoSettle = () => {
  useAutoSettleCheckoutRule(CHECKOUT_RULE_DOCUMENT_TYPES[0])
  useAutoSettleCheckoutRule(CHECKOUT_RULE_DOCUMENT_TYPES[1])
}

export default useCheckoutRuleAutoSettle
